import type { ReactNode } from 'react';
import { Link } from 'react-router';
import { useTranslation } from 'react-i18next';
import { Icon, Logo } from '../ui';

/**
 * Bar above the page on small screens (design/NavKonzept "Am Handy"): it names the level. Level 1 is dark
 * ("Ganz oben · Alle Objekte"), level 2 light with the way back and a chip "Im Objekt · <Name>" that opens the drawer
 * at the property switcher. The menu button opens the sidebar as a drawer.
 */
export function LevelTopBar({
  property,
  open,
  onMenu,
  onSwitch,
  back,
  upload,
}: {
  property: { name: string } | null;
  /** level 3: logical parent for the back arrow (else "Alle Objekte") */
  back?: { to: string; label: string } | null;
  open: boolean;
  onMenu: () => void;
  onSwitch: () => void;
  /** "+ Beleg" (icon button) next to the menu */
  upload?: ReactNode;
}) {
  const { t } = useTranslation();
  if (!property)
    return (
      <div className="level-top portfolio">
        <Link to="/" className="lt-title" aria-label={t('shell.level.home')}>
          <Logo size={26} />
          <span className="lt-text">
            <span className="lt-eyebrow">{t('shell.level.top')}</span>
            <span className="lt-name">{t('shell.level.all')}</span>
          </span>
        </Link>
        <span className="lt-end">
          {upload}
          <button
            type="button"
            className="lt-menu"
            aria-label={t('shell.level.openMenu')}
            aria-expanded={open}
            onClick={onMenu}
          >
            <Icon name="menu" size={20} strokeWidth={2} />
          </button>
        </span>
      </div>
    );
  return (
    <div className="level-top objekt">
      <Link
        to={back?.to ?? '/'}
        className="lt-back"
        aria-label={back ? t('common:ui.backTo', { label: back.label }) : t('shell.level.backAria')}
      >
        <Icon name="arrowLeft" size={20} strokeWidth={2} />
      </Link>
      <button
        type="button"
        className="lt-chip"
        aria-expanded={open}
        onClick={onSwitch}
        aria-label={t('shell.level.chipAria', { name: property.name })}
      >
        <span className="lt-text">
          <span className="lt-eyebrow">{t('shell.level.inProperty')}</span>
          <span className="lt-name truncate">{property.name}</span>
        </span>
        <Icon name="chevronDown" size={16} strokeWidth={2} />
      </button>
      {upload}
      <button
        type="button"
        className="lt-menu"
        aria-label={t('shell.level.openPropertyMenu')}
        aria-expanded={open}
        onClick={onMenu}
      >
        <Icon name="menu" size={20} strokeWidth={2} />
      </button>
    </div>
  );
}
